import React, { useState, useEffect } from 'react';
import { getData, saveData, db } from '../firebase';
import { doc, deleteDoc } from 'firebase/firestore';
import { DEFAULT_LEDGERS, generatePostings } from '../ledgers';
import { LedgerDropdown } from './Ledgers';

const BRAND = '#0f6e56';

export default function Voucher({ user }) {
  const [allData, setAllData] = useState(null);
  const [customLedgers, setCustomLedgers] = useState([]);
  const [journal, setJournal] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), fromLedger: '', toLedger: '', amount: '', narration: '' });

  const load = async () => {
    setLoading(true);
    const [invoices, expenses, pettycash, cashbook, bankbook, journalData, ledgers] = await Promise.all([
      getData(user.uid, 'invoices'), getData(user.uid, 'expenses'),
      getData(user.uid, 'pettycash'), getData(user.uid, 'cashbook'),
      getData(user.uid, 'bankbook'), getData(user.uid, 'journal'),
      getData(user.uid, 'ledgers'),
    ]);
    setAllData({ invoices, expenses, pettycash, cashbook, bankbook, journal: journalData });
    setJournal(journalData);
    setCustomLedgers(ledgers.map(l => ({ ...l, custom: true })));
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const addVoucher = async (e) => {
    e.preventDefault();
    if (!form.fromLedger || !form.toLedger || !form.amount) { alert('Please fill all fields'); return; }
    if (form.fromLedger === form.toLedger) { alert('From and To ledgers must be different'); return; }
    // Money moves FROM credit ledger TO debit ledger
    await saveData(user.uid, 'journal', {
      date: form.date,
      debitLedger: form.toLedger,
      creditLedger: form.fromLedger,
      amount: parseFloat(form.amount) || 0,
      narration: form.narration || 'Voucher entry',
    });
    setShowForm(false);
    setForm({ date: new Date().toISOString().slice(0, 10), fromLedger: '', toLedger: '', amount: '', narration: '' });
    load();
  };

  const delVoucher = async (id) => {
    if (!window.confirm('Delete this voucher?')) return;
    await deleteDoc(doc(db, 'users', user.uid, 'journal', id));
    load();
  };

  const fmt = n => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const ledgers = [...DEFAULT_LEDGERS, ...customLedgers];

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: '#aaa' }}>Loading...</div>;

  // Day book — all auto-posted entries
  const allPostings = allData ? generatePostings(allData).sort((a, b) => (b.date || '').localeCompare(a.date || '')) : [];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700 }}>Voucher Entry & Day Book</h2>
        <button onClick={() => setShowForm(true)} style={{ padding: '10px 20px', background: BRAND, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>+ New Voucher</button>
      </div>
      <div style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>Record manual entries like capital, loans or adjustments. All transactions appear in the day book below.</div>

      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e8e8e8', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e8e8e8', fontSize: 15, fontWeight: 600 }}>Day Book — all transactions ({allPostings.length})</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: '#f7f8fa' }}>
            {['Date', 'Ref', 'Narration', 'Debit ledger', 'Credit ledger', 'Amount'].map(h => (
              <th key={h} style={{ padding: '11px 16px', textAlign: h === 'Amount' ? 'right' : 'left', fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {allPostings.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: '#aaa' }}>No transactions yet</td></tr>
            ) : allPostings.map((p, i) => (
              <tr key={i} style={{ borderTop: '1px solid #f0f0f0' }}>
                <td style={td}>{p.date || '—'}</td>
                <td style={td}><span style={{ fontSize: 11, color: '#888', background: '#f0f0f0', padding: '2px 8px', borderRadius: 6 }}>{p.ref}</span></td>
                <td style={td}>{p.narration}</td>
                <td style={{ ...td, color: '#0f6e56', fontWeight: 500 }}>{p.debit}</td>
                <td style={{ ...td, color: '#a32d2d', fontWeight: 500 }}>{p.credit}</td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{fmt(p.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {journal.length > 0 && (
        <div style={{ marginTop: 20, background: '#fff', borderRadius: 14, border: '1px solid #e8e8e8', overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #e8e8e8', fontSize: 15, fontWeight: 600 }}>Manual vouchers ({journal.length})</div>
          {journal.map(j => (
            <div key={j.id} style={{ padding: '12px 20px', borderTop: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{j.narration}</div>
                <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{j.date} · {j.creditLedger} → {j.debitLedger}</div>
              </div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>{fmt(j.amount)}</div>
              <button onClick={() => delVoucher(j.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e24b4a', fontSize: 16 }}>🗑</button>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: '100%', maxWidth: 460 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 18, fontWeight: 700 }}>New Voucher Entry</div>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#888' }}>×</button>
            </div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 20 }}>Money moves from one ledger to another. Debit & credit are handled automatically.</div>
            <form onSubmit={addVoucher}>
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>Date</label>
                <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} style={inp} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>Money FROM (source) *</label>
                <LedgerDropdown ledgers={ledgers} value={form.fromLedger} onChange={v => setForm(f => ({ ...f, fromLedger: v }))} placeholder="Where money comes from..." />
              </div>
              <div style={{ textAlign: 'center', margin: '8px 0', color: BRAND, fontSize: 18 }}>↓</div>
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>Money TO (destination) *</label>
                <LedgerDropdown ledgers={ledgers} value={form.toLedger} onChange={v => setForm(f => ({ ...f, toLedger: v }))} placeholder="Where money goes to..." />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>Amount *</label>
                <input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" style={inp} />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={lbl}>Narration</label>
                <input value={form.narration} onChange={e => setForm(f => ({ ...f, narration: e.target.value }))} placeholder="e.g. Owner capital introduced" style={inp} />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" onClick={() => setShowForm(false)} style={{ flex: 1, padding: 12, borderRadius: 10, border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" style={{ flex: 2, padding: 12, background: BRAND, color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer' }}>Save Voucher</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const td = { padding: '12px 16px', fontSize: 13, color: '#444' };
const lbl = { display: 'block', fontSize: 13, fontWeight: 500, color: '#555', marginBottom: 6 };
const inp = { width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: 10, fontSize: 14, color: '#222', outline: 'none', background: '#fff' };
